import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Mail, Save, RotateCcw, Eye, Send, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle2, Loader2, Variable, Clock, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';
import { emailTemplatesAPI } from '../../utils/api';

// Template type icons
const TEMPLATE_ICONS = {
  order_shipped: '📦',
  ready_for_pickup: '🏪',
  invoice_reminder: '⏰',
  quote_send: '📝',
  invoice_send: '📄',
  statement_send: '📊',
};

// Template type descriptions
const TEMPLATE_DESCRIPTIONS = {
  order_shipped: 'Sent when an order has been shipped to the customer',
  ready_for_pickup: 'Sent when an order is ready for customer pickup',
  invoice_reminder: 'Payment reminder for outstanding invoices',
  quote_send: 'Sent when sharing a quote/proposal with customer',
  invoice_send: 'Sent when manually sending an invoice to customer',
  statement_send: 'Sent when emailing an account statement to customer',
};

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Test email modal state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Email history modal
  const [showHistory, setShowHistory] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Reset confirmation
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  // Load templates
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await emailTemplatesAPI.list();
      setTemplates(response.items || []);
      
      // Auto-select first template if none selected
      if (!selectedTemplate && response.items?.length > 0) {
        const first = response.items[0];
        setSelectedTemplate(first);
        setEditedSubject(first.subject || '');
        setEditedBody(first.body_html || '');
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate]);
  
  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Select a template
  const handleSelectTemplate = (template) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedTemplate(template);
    setEditedSubject(template.subject || '');
    setEditedBody(template.body_html || '');
    setHasUnsavedChanges(false);
  };
  
  // Track changes
  useEffect(() => {
    if (selectedTemplate) {
      const subjectChanged = editedSubject !== selectedTemplate.subject;
      const bodyChanged = editedBody !== selectedTemplate.body_html;
      setHasUnsavedChanges(subjectChanged || bodyChanged);
    }
  }, [editedSubject, editedBody, selectedTemplate]);
  
  // Save template
  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const response = await emailTemplatesAPI.update(selectedTemplate.template_type, {
        subject: editedSubject,
        body_html: editedBody,
      });
      
      // Update local state
      setTemplates(prev => prev.map(t => 
        t.template_type === selectedTemplate.template_type 
          ? response.template 
          : t
      ));
      setSelectedTemplate(response.template);
      setHasUnsavedChanges(false);
      toast.success('Template saved successfully');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to default
  const handleReset = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await emailTemplatesAPI.reset(selectedTemplate.template_type);
      
      // Update local state
      setTemplates(prev => prev.map(t => 
        t.template_type === selectedTemplate.template_type 
          ? response.template 
          : t
      ));
      setSelectedTemplate(response.template);
      setEditedSubject(response.template.subject);
      setEditedBody(response.template.body_html);
      setHasUnsavedChanges(false);
      setShowResetConfirm(false);
      toast.success('Template reset to default');
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error('Failed to reset template');
    }
  };
  
  // Preview template
  const handlePreview = async () => {
    if (!selectedTemplate) return;
    
    setPreviewLoading(true);
    setShowPreview(true);
    
    try {
      const response = await emailTemplatesAPI.preview(selectedTemplate.template_type, {
        subject: editedSubject,
        body_html: editedBody,
      });
      setPreviewData(response);
    } catch (error) {
      console.error('Failed to preview template:', error);
      toast.error('Failed to generate preview');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Send test email
  const handleSendTest = async () => {
    if (!testEmail || !selectedTemplate) return;
    
    setSendingTest(true);
    try {
      await emailTemplatesAPI.sendTest({
        to_email: testEmail,
        subject: editedSubject,
        body_html: editedBody,
      });
      toast.success(`Test email sent to ${testEmail}`);
      setShowTestModal(false);
      setTestEmail('');
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };
  
  // Toggle active status
  const handleToggleActive = async (template) => {
    try {
      const response = await emailTemplatesAPI.update(template.template_type, {
        is_active: !template.is_active,
      });
      
      setTemplates(prev => prev.map(t => 
        t.template_type === template.template_type 
          ? response.template 
          : t
      ));
      
      if (selectedTemplate?.template_type === template.template_type) {
        setSelectedTemplate(response.template);
      }
      
      toast.success(`Template ${response.template.is_active ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle template:', error);
      toast.error('Failed to update template');
    }
  };
  
  // Load email history
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        emailTemplatesAPI.getHistory({ days: 30, page_size: 50 }),
        emailTemplatesAPI.getStats({ days: 30 }),
      ]);
      setEmailLogs(logsRes.items || []);
      setEmailStats(statsRes);
    } catch (error) {
      console.error('Failed to load email history:', error);
      toast.error('Failed to load email history');
    } finally {
      setHistoryLoading(false);
    }
  };
  
  // Insert variable at cursor
  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = editedBody.substring(0, start) + `{{${variable}}}` + editedBody.substring(end);
      setEditedBody(newValue);
      
      // Reset cursor position after variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
      }, 0);
    }
  };
  
  // Get variables for selected template
  const availableVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    return selectedTemplate.variables || [];
  }, [selectedTemplate]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col" data-testid="admin-email-templates">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-ivory-800 flex items-center gap-2">
            <Mail className="w-6 h-6 text-gold-600" />
            Email Templates
          </h1>
          <p className="text-sm text-ivory-600 mt-1">
            Customize email templates for order communications
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowHistory(true);
              loadHistory();
            }}
            data-testid="view-history-btn"
          >
            <Clock className="w-4 h-4 mr-2" />
            Send History
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex gap-6 min-h-0">
        {/* Template List (Left Panel) */}
        <div className="w-72 flex-shrink-0 overflow-y-auto">
          <Card className="h-full">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-medium">Templates</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {templates.map((template) => (
                  <button
                    key={template.template_type}
                    onClick={() => handleSelectTemplate(template)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-all ${
                      selectedTemplate?.template_type === template.template_type
                        ? 'bg-gold-100 border border-gold-300'
                        : 'hover:bg-ivory-100 border border-transparent'
                    }`}
                    data-testid={`template-item-${template.template_type}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{TEMPLATE_ICONS[template.template_type]}</span>
                        <span className="font-medium text-sm">
                          {template.type_label || template.template_type}
                        </span>
                      </div>
                      {!template.is_active && (
                        <Badge variant="secondary" className="text-xs">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-ivory-500 mt-1 line-clamp-2">
                      {TEMPLATE_DESCRIPTIONS[template.template_type]}
                    </p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Editor Panel (Right) */}
        <div className="flex-1 flex flex-col min-h-0">
          {selectedTemplate ? (
            <Card className="flex-1 flex flex-col">
              <CardHeader className="py-3 px-4 flex-shrink-0 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-xl">{TEMPLATE_ICONS[selectedTemplate.template_type]}</span>
                      {selectedTemplate.type_label}
                      {hasUnsavedChanges && (
                        <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                          Unsaved
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {TEMPLATE_DESCRIPTIONS[selectedTemplate.template_type]}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 pr-4 border-r">
                      <Label htmlFor="active-switch" className="text-sm">Active</Label>
                      <Switch
                        id="active-switch"
                        checked={selectedTemplate.is_active}
                        onCheckedChange={() => handleToggleActive(selectedTemplate)}
                        data-testid="template-active-toggle"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowResetConfirm(true)}
                      data-testid="reset-template-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      data-testid="preview-template-btn"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTestModal(true)}
                      data-testid="send-test-btn"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Test
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={!hasUnsavedChanges || saving}
                      data-testid="save-template-btn"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-1" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  {/* Subject Line */}
                  <div>
                    <Label htmlFor="template-subject" className="text-sm font-medium">
                      Subject Line
                    </Label>
                    <Input
                      id="template-subject"
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      placeholder="Enter email subject..."
                      className="mt-1"
                      data-testid="template-subject-input"
                    />
                    <p className="text-xs text-ivory-500 mt-1">
                      Use {"{{variable}}"} syntax for dynamic content
                    </p>
                  </div>
                  
                  {/* Body Editor */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="template-body" className="text-sm font-medium">
                        Email Body (HTML)
                      </Label>
                      <span className="text-xs text-ivory-500">
                        v{selectedTemplate.version || 1}
                      </span>
                    </div>
                    <Textarea
                      id="template-body"
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      placeholder="Enter email body HTML..."
                      className="mt-1 font-mono text-sm min-h-[300px]"
                      data-testid="template-body-input"
                    />
                  </div>
                  
                  {/* Variables Panel */}
                  <div className="bg-ivory-50 rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-3">
                      <Variable className="w-4 h-4 text-gold-600" />
                      <span className="text-sm font-medium">Available Variables</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {availableVariables.map((variable) => (
                        <button
                          key={variable}
                          onClick={() => insertVariable(variable)}
                          className="px-2 py-1 text-xs bg-white border rounded hover:bg-gold-50 hover:border-gold-300 transition-colors font-mono"
                          title={`Click to insert {{${variable}}}`}
                        >
                          {`{{${variable}}}`}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-ivory-500 mt-2">
                      Click a variable to insert it at the cursor position
                    </p>
                  </div>
                  
                  {/* Auto-send Settings */}
                  {selectedTemplate.template_type !== 'invoice_reminder' && (
                    <div className="bg-ivory-50 rounded-lg p-4 border">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Auto-send on Status Change</p>
                          <p className="text-xs text-ivory-500">
                            Automatically send this email when order status changes
                          </p>
                        </div>
                        <Switch
                          checked={selectedTemplate.auto_send_enabled || false}
                          onCheckedChange={async (checked) => {
                            try {
                              const response = await emailTemplatesAPI.update(
                                selectedTemplate.template_type,
                                { auto_send_enabled: checked }
                              );
                              setTemplates(prev => prev.map(t =>
                                t.template_type === selectedTemplate.template_type
                                  ? response.template
                                  : t
                              ));
                              setSelectedTemplate(response.template);
                              toast.success(`Auto-send ${checked ? 'enabled' : 'disabled'}`);
                            } catch (error) {
                              toast.error('Failed to update auto-send setting');
                            }
                          }}
                          data-testid="auto-send-toggle"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="flex-1 flex items-center justify-center">
              <div className="text-center text-ivory-500">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a template to edit</p>
              </div>
            </Card>
          )}
        </div>
      </div>
      
      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Preview with sample data
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
            </div>
          ) : previewData ? (
            <Tabs defaultValue="rendered" className="flex-1 overflow-hidden flex flex-col">
              <TabsList>
                <TabsTrigger value="rendered">Rendered</TabsTrigger>
                <TabsTrigger value="html">HTML Source</TabsTrigger>
                <TabsTrigger value="data">Sample Data</TabsTrigger>
              </TabsList>
              <TabsContent value="rendered" className="flex-1 overflow-hidden">
                <div className="border rounded-lg overflow-hidden h-full">
                  <div className="bg-ivory-100 px-4 py-2 border-b">
                    <p className="text-sm">
                      <strong>Subject:</strong> {previewData.rendered_subject}
                    </p>
                  </div>
                  <iframe
                    srcDoc={previewData.full_html}
                    className="w-full h-[400px] bg-white"
                    title="Email Preview"
                    data-testid="preview-iframe"
                  />
                </div>
              </TabsContent>
              <TabsContent value="html" className="flex-1 overflow-auto">
                <pre className="text-xs bg-ivory-50 p-4 rounded-lg overflow-auto max-h-[400px]">
                  {previewData.full_html}
                </pre>
              </TabsContent>
              <TabsContent value="data" className="flex-1 overflow-auto">
                <div className="space-y-2 max-h-[400px] overflow-auto">
                  {Object.entries(previewData.sample_data || {}).map(([key, value]) => (
                    <div key={key} className="flex gap-4 text-sm">
                      <code className="text-gold-600 font-mono">{`{{${key}}}`}</code>
                      <span className="text-ivory-600">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>
      
      {/* Test Email Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email to verify the template renders correctly
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-email">Recipient Email</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address..."
                data-testid="test-email-input"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  This will send the current template with sample data. 
                  Any unsaved changes will be included in the test email.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendTest} 
              disabled={!testEmail || sendingTest}
              data-testid="confirm-send-test-btn"
            >
              {sendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reset Confirmation Modal */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Template?</DialogTitle>
            <DialogDescription>
              This will reset the template to its default content. 
              Any customizations will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReset}>
              Reset to Default
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Email History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Email Send History</DialogTitle>
            <DialogDescription>
              Recent emails sent in the last 30 days
            </DialogDescription>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              {emailStats && (
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="text-2xl font-bold">{emailStats.total_sent}</p>
                          <p className="text-xs text-ivory-500">Emails Sent</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <div>
                          <p className="text-2xl font-bold">{emailStats.total_failed}</p>
                          <p className="text-xs text-ivory-500">Failed</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-gold-500" />
                        <div>
                          <p className="text-2xl font-bold">{emailStats.total}</p>
                          <p className="text-xs text-ivory-500">Total</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
              
              {/* Logs Table */}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ivory-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-left font-medium">Template</th>
                      <th className="px-3 py-2 text-left font-medium">Recipient</th>
                      <th className="px-3 py-2 text-left font-medium">Subject</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {emailLogs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-ivory-500">
                          No emails sent yet
                        </td>
                      </tr>
                    ) : (
                      emailLogs.map((log, idx) => (
                        <tr key={log.id || idx} className="hover:bg-ivory-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {new Date(log.sent_at).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline">
                              {TEMPLATE_ICONS[log.template_type]} {log.template_type}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">{log.to_email}</td>
                          <td className="px-3 py-2 max-w-xs truncate">{log.subject}</td>
                          <td className="px-3 py-2">
                            {log.status === 'sent' ? (
                              <Badge className="bg-green-100 text-green-700">Sent</Badge>
                            ) : (
                              <Badge variant="destructive">Failed</Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailTemplates;
